import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import ErrorCard from '../ErrorCard';

const Container = styled.div`
  margin: 32px 0;
`;

const ConsentBox = styled.div`
  background-color: #FFFFFF;
  padding: 24px;
  border-radius: var(--zyvgo-radius-md, 8px);
  border: 2px solid var(--zyvgo-border, #e1e5e9);
  margin-bottom: 24px;
  max-height: 400px;
  overflow-y: auto;
  position: relative;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ConsentText = styled.div`
  line-height: 1.7;
  font-size: 0.9375rem;
  color: var(--zyvgo-text-primary, #222222);
  
  p {
    margin: 0 0 16px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  h3, h4 {
    margin: 20px 0 12px 0;
    font-weight: 600;
    color: var(--zyvgo-text-primary, #222222);
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  ul, ol {
    margin: 12px 0;
    padding-left: 24px;
  }
  
  li {
    margin-bottom: 8px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px;
  background-color: ${props => props.$isChecked ? '#F0FFF4' : '#FFFFFF'};
  border: 2px solid ${props => props.$isChecked ? 'var(--zyvgo-success, #50b37a)' : 'var(--zyvgo-border, #e1e5e9)'};
  border-radius: var(--zyvgo-radius-md, 8px);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.$isChecked ? 'var(--zyvgo-success, #50b37a)' : 'var(--zyvgo-primary, #222222)'};
    background-color: ${props => props.$isChecked ? '#F0FFF4' : 'rgba(34, 34, 34, 0.02)'};
  }
`;

const CheckboxInput = styled.input`
  width: 22px;
  height: 22px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--zyvgo-success, #50b37a);
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9375rem;
  line-height: 1.6;
  cursor: pointer;
  font-weight: 500;
  color: var(--zyvgo-text-primary, #222222);
  flex: 1;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.95), transparent);
  pointer-events: none;
  display: ${props => props.$show ? 'block' : 'none'};
  border-radius: 0 0 var(--zyvgo-radius-md, 8px) var(--zyvgo-radius-md, 8px);
`;

const getConsentChoiceLabel = (choice) => {
  if (typeof choice === 'string') return choice;
  if (choice && typeof choice === 'object') {
    return choice.text || choice.label || choice.value || '';
  }
  return '';
};

const getConsentChoiceValue = (choice) => {
  if (typeof choice === 'string') return choice;
  if (choice && typeof choice === 'object') {
    return choice.value || choice.id || choice.text || choice.label || '';
  }
  return '';
};

const isDisqualifyingChoice = (choice) => (
  choice && typeof choice === 'object' && (
    choice.disqualifies === true
    || choice.is_disqualifying === true
    || choice.isDisqualifying === true
  )
);

const hasDisqualifyingMetadata = (choice) => (
  choice && typeof choice === 'object' && (
    Object.prototype.hasOwnProperty.call(choice, 'disqualifies')
    || Object.prototype.hasOwnProperty.call(choice, 'is_disqualifying')
    || Object.prototype.hasOwnProperty.call(choice, 'isDisqualifying')
  )
);

const ConsentForm = ({ question, value, onChange, error }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const consentBoxRef = useRef(null);

  useEffect(() => {
    const consentBox = consentBoxRef.current;
    if (!consentBox) return;

    // Check if content is scrollable
    const isScrollable = consentBox.scrollHeight > consentBox.clientHeight;
    
    if (!isScrollable) {
      // If content fits without scrolling, allow checkbox immediately
      setHasScrolledToBottom(true);
      setShowScrollIndicator(false);
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = consentBox;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      
      if (scrolledToBottom) {
        setHasScrolledToBottom(true);
        setShowScrollIndicator(false);
      }
    };

    consentBox.addEventListener('scroll', handleScroll);
    
    // Initial check
    handleScroll();

    return () => {
      consentBox.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const rawConsentBody =
    question.consent_form?.consent_text
    || question.consent_form?.text
    || question.consent_text
    || question.validation_rules?.consent_form?.consent_text
    || question.validation_rules?.consent_form?.text
    || question.validation_rules?.element_config?.consent_text
    || (question.text && question.text !== question.question_text && question.text !== question.name ? question.text : '')
    || '';

  const cleanConsentHtml = useMemo(() => {
    let body = (rawConsentBody || '').trim();

    const titles = [
      question.question_text,
      question.name,
      question.consent_form?.name,
      question.consent_header,
      question.title
    ]
      .filter((t) => typeof t === 'string' && t.trim().length > 0)
      .map((t) => t.trim());

    const uniqueTitles = [...new Set(titles)];

    for (const title of uniqueTitles) {
      if (!title || !body) continue;
      const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // 1. Match HTML header/paragraph wrappers with optional attributes & inner tags (e.g. <h1><strong>Title</strong></h1>, <p class="...">Title:</p>)
      const htmlHeadingPattern = new RegExp(
        `^<([a-z0-9]+)[^>]*>(?:<[^>]+>)*\\s*${escapedTitle}\\s*[:\\-]?\\s*(?:</[^>]+>)*</\\1>\\s*`,
        'i'
      );
      body = body.replace(htmlHeadingPattern, '');

      // 2. Match Markdown headings (e.g. # Title, ## Title)
      const markdownHeadingPattern = new RegExp(
        `^#{1,6}\\s*${escapedTitle}\\s*[:\\-]?\\s*(\n|<br\\s*/?>)*`,
        'i'
      );
      body = body.replace(markdownHeadingPattern, '');

      // 3. Match plain text leading title followed by newlines, <br>, or punctuation
      const plainHeadingPattern = new RegExp(
        `^${escapedTitle}\\s*[:\\-]?\\s*(\n+|<br\\s*/?>)+`,
        'i'
      );
      body = body.replace(plainHeadingPattern, '');
    }

    body = body.trim();

    if (!body) {
      const rawTrimmed = (rawConsentBody || '').trim();
      const isTitleDuplicate = uniqueTitles.some((t) => t.toLowerCase() === rawTrimmed.toLowerCase());
      const finalResult = (!isTitleDuplicate && rawTrimmed) || '<p>Please review the terms of this consent carefully.</p>';
      return finalResult;
    }

    return body;
  }, [
    rawConsentBody,
    question.question_text,
    question.name,
    question.consent_form?.name,
    question.consent_header,
    question.title
  ]);

  const handleChange = (selectedChoice, isAgree) => {
    if (hasScrolledToBottom) {
      const selectedChoiceLabel = getConsentChoiceLabel(selectedChoice);
      // Send the actual consent response with full text
      // isAgree follows the explicit disqualifying flag when available and
      // otherwise uses the legacy first-choice convention.
      const consentResponse = {
        agreed: isAgree,
        consent_text: selectedChoiceLabel,
        selected_value: getConsentChoiceValue(selectedChoice),
        consent_type: question.consent_form?.consent_type || 'custom',
        consent_header: question.question_text || question.name || question.consent_form?.name || 'Consent'
      };
      onChange(consentResponse);
    }
  };

  const handleContainerClick = (choiceText, isAgree) => {
    if (hasScrolledToBottom) {
      handleChange(choiceText, isAgree);
    }
  };

  return (
    <Container>
      <ConsentBox ref={consentBoxRef}>
        <ConsentText
          dangerouslySetInnerHTML={{
            __html: cleanConsentHtml
          }}
        />
        <ScrollIndicator $show={showScrollIndicator} />
      </ConsentBox>
      
      {hasScrolledToBottom ? (
        <>
          {/* Render answer choices from question data */}
          {question.answer_choices && question.answer_choices.length > 0 ? (
            question.answer_choices.map((choice, index) => {
              // Preserve the legacy first-choice convention, but honor the
              // explicit disqualifying flag when consent options provide it.
              const isAgreeOption = hasDisqualifyingMetadata(choice)
                ? !isDisqualifyingChoice(choice)
                : index === 0;
              const choiceLabel = getConsentChoiceLabel(choice);
              const choiceValue = getConsentChoiceValue(choice);
              const isSelected =
                value?.consent_text === choiceLabel ||
                value?.consent_text === choiceValue;
              
              return (
                <CheckboxContainer 
                  key={choiceValue || choiceLabel || index}
                  onClick={() => handleContainerClick(choice, isAgreeOption)}
                  $isChecked={isSelected}
                  style={{ marginBottom: index < question.answer_choices.length - 1 ? '12px' : '0' }}
                >
                  <CheckboxInput
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        handleChange(choice, isAgreeOption);
                      }
                    }}
                  />
                  <CheckboxLabel onClick={(e) => e.stopPropagation()}>
                    {choiceLabel}
                  </CheckboxLabel>
                </CheckboxContainer>
              );
            })
          ) : (
            /* Fallback if no answer_choices defined */
            <CheckboxContainer 
              onClick={() => handleContainerClick("I acknowledge that I have read and understood the above information", true)}
              $isChecked={value?.agreed === true || value === true}
            >
              <CheckboxInput
                type="checkbox"
                checked={value?.agreed === true || value === true}
                onChange={(e) => {
                  e.stopPropagation();
                  if (e.target.checked) {
                    handleChange("I acknowledge that I have read and understood the above information", true);
                  }
                }}
              />
              <CheckboxLabel onClick={(e) => e.stopPropagation()}>
                I acknowledge that I have read and understood the above information
              </CheckboxLabel>
            </CheckboxContainer>
          )}
        </>
      ) : (
        <ErrorCard
          message="Please scroll to the bottom to read the complete consent form before continuing."
          variant="info"
        />
      )}
      
      {error && hasScrolledToBottom && (
        <ErrorCard
          message={error}
          variant="error"
        />
      )}
    </Container>
  );
};

export default ConsentForm;
